import { isDef, isDefAndNotNull } from '@utils/def';
import {
  ChangeEvent,
  FocusEvent,
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ICONS_TYPES } from '../Icon';
import Loading from '../Loading';

import { InputTypes, Sizes } from './enums';
import {
  AttentionIcon,
  ClearAdornmentWrapper,
  ErrorMessage,
  ErrorMessageContainer,
  InputContainer,
  Label,
  LoadingAdornmentWrapper,
  RequiredMark,
  Root,
  StyledIcon,
  StyledInputAdornment,
  StyledTextField,
} from './styles';
import { Props } from './types';

const Input = (
  {
    className,
    disabled,
    error,
    extraRightContent,
    successStatus,
    fullWidth = true,
    label,
    loading,
    multiline,
    name,
    autoComplete = false,
    onBlur,
    onChange,
    onClick,
    placeholder,
    postfix,
    prefix,
    readOnly = false,
    required,
    rows = 2,
    maxRows,
    secure,
    showLabel = false,
    size = Sizes.medium,
    step = 1,
    type = InputTypes.text,
    value,
    variant,
  }: Props,
  ref?: Ref<unknown>,
) => {
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const caretRef = useRef<{
    element: HTMLInputElement | HTMLTextAreaElement;
    significantCharsBeforeCaret: number;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const disabledOrLoading = disabled || loading;
  const formattedValue = useMemo(() => {
    if (!isDefAndNotNull(value)) {
      return '';
    }
    if (type === InputTypes.number) {
      const stringValue = String(value);
      const isNegative = stringValue.startsWith('-');
      const formattedNumberValue = stringValue.replace(/[^\d.]/g, '');
      const isFloat = !!(formattedNumberValue.match(/\./g) || []).length;

      if (!isFloat) {
        const formatted = stringValue
          .replace(/[^\d]/g, '')
          .replace(/(\d)(?=(\d{3})+$)/g, '$1,');
        return isNegative ? `-${formatted}` : formatted;
      } else if (isFloat) {
        const splitFloat = stringValue.split('.');
        splitFloat[0] = splitFloat[0]
          .replace(/[^\d-]/g, '')
          .replace(/(\d)(?=(\d{3})+$)/g, '$1,');

        return `${splitFloat[0]}.${splitFloat[1]}`;
      } else {
        return value;
      }
    }

    return value;
  }, [type, value]);
  const isError = useMemo(() => isDefAndNotNull(error), [error]);
  const isSuccess = useMemo(
    () => isDefAndNotNull(successStatus) && successStatus && !isError,
    [isError, successStatus],
  );
  useImperativeHandle(ref, () => inputRef.current);

  // put the caret back after the same digit it was after before the value was formatted
  useLayoutEffect(() => {
    const caret = caretRef.current;

    caretRef.current = null;

    if (!caret || caret.element !== document.activeElement) return;

    const displayedValue = caret.element.value;
    let position = 0;
    let significantChars = 0;

    while (
      position < displayedValue.length &&
      significantChars < caret.significantCharsBeforeCaret
    ) {
      if (displayedValue[position] !== ',') significantChars += 1;
      position += 1;
    }

    caret.element.setSelectionRange(position, position);
  });

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      const value = e.target.value;

      if (type === InputTypes.number) {
        let formattedValue = value
          .replace(/[^\d.-]/g, '')
          .replace(
            /([^.]*)\.(.*)/,
            (_, beforeDot, afterDot) =>
              `${beforeDot}.${afterDot.replace(/\./g, '')}`,
          );

        if (formattedValue.length > 1 && formattedValue[0] === '.') {
          formattedValue = `0${formattedValue}`;
        }

        // remember the caret as "after N digits", the separators of the displayed value are not counted
        const typedBeforeCaret = value
          .slice(0, e.target.selectionStart ?? value.length)
          .replace(/[^\d.-]/g, '').length;
        const addedLeadingZero =
          formattedValue.length > 1 &&
          formattedValue[0] === '0' &&
          !value.replace(/[^\d.-]/g, '').startsWith('0');

        caretRef.current = {
          element: e.target,
          significantCharsBeforeCaret:
            typedBeforeCaret +
            (addedLeadingZero && typedBeforeCaret > 0 ? 1 : 0),
        };

        onChange?.(formattedValue);
      } else {
        onChange?.(value);
      }
    },
    [onChange, type],
  );
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      onBlur?.(e.target.value);
    },
    [onBlur],
  );
  const handleClear = useCallback(
    (_: React.MouseEvent<HTMLElement, MouseEvent> | KeyboardEvent) => {
      onChange?.('');
    },
    [onChange],
  );
  const loadingAdornment = useMemo(
    () => (
      <LoadingAdornmentWrapper>
        <Loading size={14} />
      </LoadingAdornmentWrapper>
    ),
    [],
  );
  const clearAdornment = useMemo(
    () =>
      isDefAndNotNull(formattedValue) &&
      formattedValue !== '' &&
      !readOnly &&
      !disabledOrLoading && (
        <ClearAdornmentWrapper>
          <StyledIcon
            icon={ICONS_TYPES.XMark}
            size={12}
            onClick={handleClear}
          />
        </ClearAdornmentWrapper>
      ),
    [disabledOrLoading, formattedValue, handleClear, readOnly],
  );
  const toggleSecureVisibility = useCallback(
    () => setShowPassword((prev) => !prev),
    [],
  );
  const secureAdornment = useMemo(
    () =>
      secure &&
      !disabledOrLoading && (
        <StyledIcon
          icon={showPassword ? ICONS_TYPES.EyeOff : ICONS_TYPES.Eye}
          size={16}
          onClick={toggleSecureVisibility}
        />
      ),
    [disabledOrLoading, secure, showPassword, toggleSecureVisibility],
  );
  const totalAdornment = useMemo(
    () => (
      <StyledInputAdornment position="end" $error={isError ? true : undefined}>
        {clearAdornment}
        {secureAdornment}
        {isDefAndNotNull(postfix) && postfix}
      </StyledInputAdornment>
    ),
    [clearAdornment, isError, postfix, secureAdornment],
  );

  return (
    <Root className={className}>
      {label && showLabel && (
        <Label htmlFor={name} error={isError}>
          {required && !disabledOrLoading ? (
            <span>
              {label}
              <RequiredMark error={isError}>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </Label>
      )}
      <InputContainer disabled={disabled} onClick={onClick}>
        <StyledTextField
          name={name}
          autoComplete={autoComplete ? name : undefined}
          sizetype={size}
          fullWidth={fullWidth}
          type={
            secure
              ? showPassword
                ? InputTypes.text
                : InputTypes.password
              : type === InputTypes.number
                ? InputTypes.text
                : type
          }
          required={required}
          disabled={disabled}
          error={isError}
          $isSuccess={isSuccess}
          label={showLabel ? undefined : label}
          multiline={multiline}
          minRows={rows}
          maxRows={maxRows ?? rows}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          value={formattedValue}
          inputProps={{
            step,
          }}
          InputProps={{
            startAdornment: prefix && (
              <StyledInputAdornment
                position="start"
                $error={isError ? true : undefined}>
                <>{prefix}</>
              </StyledInputAdornment>
            ),
            endAdornment: loading ? loadingAdornment : totalAdornment,
            readOnly: readOnly,
          }}
          variant={isDef(variant) ? variant : undefined}
        />
        {isDefAndNotNull(extraRightContent) && extraRightContent}
      </InputContainer>
      {isError && error !== '' && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props };
export { InputTypes, Sizes };

export default forwardRef(Input);
